import React, { useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import publisherBillingService from '../services/publisherBillingService';
import { format } from 'date-fns';

const PublisherBillingPage: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const [selectedMonth, setSelectedMonth] = useState<number | ''>('');
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  const { data: billingData, isLoading, error } = useQuery({
    queryKey: ['publisher-billing', selectedMonth, selectedYear],
    queryFn: () => publisherBillingService.getMonthlyBilling(
      selectedMonth || undefined,
      selectedYear
    ),
  });

  const billing = billingData?.data;

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleDownloadPDF = () => {
    if (!billing) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Publisher Billing Report', pageWidth / 2, 15, { align: 'center' });
    
    // Report Info
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(`Generated: ${reportDate}`, 14, 25);
    
    let filterText = 'Period: ';
    if (selectedMonth) {
      const monthName = months.find(m => m.value === selectedMonth)?.label || '';
      filterText += `${monthName} ${selectedYear}`;
    } else {
      filterText += `All Months, ${selectedYear}`;
    }
    doc.text(filterText, 14, 30);
    
    let yPos = 40;

    // Summary Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 14, yPos);
    yPos += 7;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Earnings: ${formatCurrency(billing.overallTotal)}`, 14, yPos);
    yPos += 5;
    doc.text(`Total Placements: ${billing.totalPlacements}`, 14, yPos);
    yPos += 5;
    doc.text(`Active Months: ${billing.monthlyTotals.length}`, 14, yPos);
    yPos += 10;

    // Monthly Totals Table
    if (billing.monthlyTotals.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Summary', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Month', 'Year', 'Earnings', 'Placements']],
        body: billing.monthlyTotals.map(m => [
          m.month,
          m.year.toString(),
          formatCurrency(m.total),
          m.count.toString()
        ]),
        theme: 'grid',
        headStyles: { fillColor: [76, 175, 80], fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        margin: { left: 14 },
        tableWidth: 'auto',
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Placement Details Table
    if (billing.placements.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Billing Details', 14, yPos);
      yPos += 5;

      autoTable(doc, {
        startY: yPos,
        head: [['Order #', 'Order Date', 'Site URL', 'Type', 'Earning']],
        body: billing.placements.map(p => [
          p.orderNumber,
          formatDate(p.orderDate),
          p.siteUrl.length > 30 ? p.siteUrl.substring(0, 30) + '...' : p.siteUrl,
          p.submissionType,
          formatCurrency(p.basePrice)
        ]),
        theme: 'striped',
        headStyles: { fillColor: [76, 175, 80], fontSize: 8 },
        bodyStyles: { fontSize: 7 },
        margin: { left: 14 },
        tableWidth: 'auto',
      });
    }

    // Generate filename
    let filename = 'publisher-billing';
    if (selectedMonth) {
      const monthName = months.find(m => m.value === selectedMonth)?.label.toLowerCase() || '';
      filename += `-${monthName}`;
    }
    filename += `-${selectedYear}.pdf`;

    doc.save(filename);
  };

  return (
    <Box className="page">
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        
        .page { padding: 22px; display: flex; flex-direction: column; gap: 18px; }
        
        .ph { display: flex; flex-direction: column; gap: 3px; }
        .ph-title { font-size: 19px; font-weight: 700; letter-spacing: -0.4px; color: #111827; }
        .ph-sub { font-size: 13px; color: #6B7280; }
        
        .filter-card {
          background: #FFFFFF; border: 1px solid #E5E7EB;
          border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          padding: 16px 18px;
          display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;
        }
        .filter-section-label { font-size: 10.5px; font-weight: 600; color: #9CA3AF; letter-spacing: 0.6px; text-transform: uppercase; margin-bottom: 2px; }
        .fc-left { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; flex: 1; }
        .fc-right { margin-left: auto; flex-shrink: 0; }
        .filter-label-group { display: flex; flex-direction: column; gap: 4px; }
        
        .fsel {
          appearance: none; border: 1px solid #E5E7EB; border-radius: 6px;
          padding: 8px 30px 8px 12px; font-family: inherit; font-size: 13px;
          color: #374151; background: #FFFFFF; outline: none; cursor: pointer;
          transition: all 0.14s;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%239CA3AF' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 10px center;
        }
        .fsel:focus { border-color: #4F46E5; box-shadow: 0 0 0 3px rgba(79,70,229,0.08); }
        .fsel:disabled { opacity: 0.5; cursor: not-allowed; background-color: #F9FAFB; }
        
        .btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 6px; font-family: inherit; font-size: 12.5px; font-weight: 500; cursor: pointer; border: none; transition: all 0.14s; white-space: nowrap; line-height: 1; }
        .btn svg { width: 13px; height: 13px; flex-shrink: 0; }
        .btn-primary { background: #4F46E5; color: #fff; box-shadow: 0 1px 2px rgba(79,70,229,0.25); }
        .btn-primary:hover:not(:disabled) { background: #4338CA; box-shadow: 0 3px 8px rgba(79,70,229,0.3); transform: translateY(-1px); }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        
        .stats-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
        .gcard {
          border-radius: 12px; padding: 24px 26px;
          position: relative; overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.12);
          transition: transform 0.18s, box-shadow 0.18s; cursor: default;
        }
        .gcard:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(0,0,0,0.16); }
        .gc-earnings { background: linear-gradient(135deg, #10B981 0%, #059669 50%, #047857 100%); }
        .gc-orders { background: linear-gradient(135deg, #6366F1 0%, #4F46E5 50%, #4338CA 100%); }
        .gc-months { background: linear-gradient(135deg, #06B6D4 0%, #0EA5E9 60%, #0284C7 100%); }
        
        .gcard-inner { position: relative; z-index: 1; }
        .gcard-top { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
        .gcard-icon {
          width: 36px; height: 36px; border-radius: 9px;
          background: rgba(255,255,255,0.18);
          display: flex; align-items: center; justify-content: center;
        }
        .gcard-icon svg { width: 18px; height: 18px; color: #fff; }
        .gcard-label { font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.9); letter-spacing: -0.1px; }
        .gcard-value { font-size: 30px; font-weight: 700; color: #fff; letter-spacing: -0.8px; line-height: 1; font-family: 'JetBrains Mono', monospace; }
        .gcard-sub { font-size: 12px; color: rgba(255,255,255,0.7); margin-top: 6px; font-weight: 400; }
        .gcard::before, .gcard::after { content: ''; position: absolute; border-radius: 50%; background: rgba(255,255,255,0.07); pointer-events: none; }
        .gcard::before { width: 140px; height: 140px; top: -40px; right: -30px; }
        .gcard::after { width: 90px; height: 90px; bottom: -20px; right: 40px; }
        
        .section-title { font-size: 15px; font-weight: 700; color: #111827; letter-spacing: -0.2px; margin-bottom: 14px; }
        
        .monthly-grid { display: flex; gap: 12px; flex-wrap: wrap; }
        .monthly-card {
          background: #FFFFFF; border: 1px solid #E5E7EB;
          border-radius: 8px; padding: 14px 18px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05); min-width: 170px;
          transition: box-shadow 0.15s; cursor: default;
        }
        .monthly-card:hover { box-shadow: 0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04); }
        .mc-month { font-size: 12px; color: #6B7280; margin-bottom: 6px; font-weight: 500; }
        .mc-amount { font-size: 17px; font-weight: 700; color: #10B981; font-family: 'JetBrains Mono', monospace; letter-spacing: -0.3px; }
        .mc-placements { font-size: 11.5px; color: #9CA3AF; margin-top: 4px; }
        
        .details-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 8px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); overflow: hidden; }
        .details-header { padding: 16px 20px; border-bottom: 1px solid #E5E7EB; }
        .dtscroll { overflow-x: auto; }
        .dtable { width: 100%; border-collapse: collapse; min-width: 760px; }
        .dtable thead th {
          text-align: left; font-size: 10.5px; font-weight: 600; color: #9CA3AF;
          letter-spacing: 0.5px; text-transform: uppercase; padding: 9px 14px;
          border-bottom: 1px solid #E5E7EB; background: #FAFAFA;
          white-space: nowrap;
        }
        .dtable thead th:first-child { padding-left: 16px; }
        .dtable thead th:last-child { text-align: right; padding-right: 16px; }
        .dtable tbody tr { transition: background 0.08s; }
        .dtable tbody tr:hover { background: #FAFBFF; }
        .dtable tbody td {
          padding: 10px 14px; font-size: 12.5px;
          border-bottom: 1px solid #F3F4F6;
          vertical-align: middle; white-space: nowrap;
        }
        .dtable tbody tr:last-child td { border-bottom: none; }
        .dtable tbody td:first-child { padding-left: 16px; }
        .dtable tbody td:last-child { text-align: right; padding-right: 16px; }
        
        .c-order { font-family: 'JetBrains Mono', monospace; font-size: 11.5px; font-weight: 500; color: #6B7280; }
        .c-date { font-size: 12.5px; color: #374151; }
        .c-url { color: #4F46E5; font-size: 12.5px; font-weight: 450; text-decoration: none; max-width: 250px; overflow: hidden; text-overflow: ellipsis; display: block; }
        .c-url:hover { text-decoration: underline; }
        .c-earn { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 700; color: #10B981; }
        
        .type-badge { display: inline-block; padding: 3px 9px; border-radius: 5px; font-size: 11px; font-weight: 700; letter-spacing: 0.3px; }
        .tb-gbs { background: #4F46E5; color: #fff; }
        .tb-ext { background: #0D9488; color: #fff; }
        .tb-pr { background: #7C3AED; color: #fff; }
        
        .status-badge { display: inline-block; padding: 3px 9px; border-radius: 5px; font-size: 11px; font-weight: 700; letter-spacing: 0.3px; background: #10B981; color: #fff; }
        
        .dtable tfoot td {
          padding: 10px 14px; font-size: 12.5px;
          border-top: 2px solid #E5E7EB;
          font-weight: 600; color: #111827;
        }
        .dtable tfoot td:first-child { padding-left: 16px; }
        .dtable tfoot td:last-child { text-align: right; padding-right: 16px; font-family: 'JetBrains Mono', monospace; font-size: 13.5px; font-weight: 700; color: #10B981; }
        
        .empty-state { padding: 60px 20px; text-align: center; }
        .empty-icon { width: 48px; height: 48px; margin: 0 auto 12px; color: #D1D5DB; }
        .empty-title { font-size: 14px; font-weight: 600; color: #6B7280; margin-bottom: 4px; }
        .empty-text { font-size: 13px; color: #9CA3AF; }
      `}</style>

      {/* Page header */}
      <div className="ph">
        <h1 className="ph-title">Monthly Billing</h1>
        <p className="ph-sub">View your earnings breakdown by month</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-row">
        <div className="gcard gc-earnings">
          <div className="gcard-inner">
            <div className="gcard-top">
              <div className="gcard-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <span className="gcard-label">Total Earnings</span>
            </div>
            <div className="gcard-value">${billing?.overallTotal.toFixed(2) || '0.00'}</div>
            <div className="gcard-sub">Across all completed orders</div>
          </div>
        </div>
        <div className="gcard gc-orders">
          <div className="gcard-inner">
            <div className="gcard-top">
              <div className="gcard-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <span className="gcard-label">Completed Orders</span>
            </div>
            <div className="gcard-value">{billing?.totalPlacements || 0}</div>
            <div className="gcard-sub">Total placements completed</div>
          </div>
        </div>
        <div className="gcard gc-months">
          <div className="gcard-inner">
            <div className="gcard-top">
              <div className="gcard-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <span className="gcard-label">Active Months</span>
            </div>
            <div className="gcard-value">{billing?.monthlyTotals.length || 0}</div>
            <div className="gcard-sub">Months with earnings</div>
          </div>
        </div>
      </div>

      {/* Filter card */}
      <div className="filter-card">
        <div className="fc-left">
          <div className="filter-label-group">
            <span className="filter-section-label">Filters</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <select
                className="fsel"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value ? Number(e.target.value) : '')}
                style={{ minWidth: '150px' }}
              >
                <option value="">All Months</option>
                {months.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              <select
                className="fsel"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="fc-right">
          <button 
            className="btn btn-primary" 
            onClick={handleDownloadPDF}
            disabled={!billing || billing.placements.length === 0}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download PDF
          </button>
        </div>
      </div>

      {/* Monthly Summary */}
      {billing && billing.monthlyTotals.length > 0 && (
        <div>
          <h2 className="section-title">Monthly Summary</h2>
          <div className="monthly-grid">
            {billing.monthlyTotals.map((monthData, index) => (
              <div key={index} className="monthly-card">
                <div className="mc-month">{monthData.month} {monthData.year}</div>
                <div className="mc-amount">${monthData.total.toFixed(2)}</div>
                <div className="mc-placements">{monthData.count} order{monthData.count !== 1 ? 's' : ''}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Billing Details */}
      <div className="details-card">
        <div className="details-header">
          <h2 className="section-title" style={{ marginBottom: 0 }}>Billing Details</h2>
        </div>

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load billing data. Please try again.
          </Alert>
        ) : !billing || billing.placements.length === 0 ? (
          <div className="empty-state">
            <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <div className="empty-title">No billing records found</div>
            <div className="empty-text">No billing records found for the selected period.</div>
          </div>
        ) : (
          <div className="dtscroll">
            <table className="dtable">
              <thead>
                <tr>
                  <th>Order Number</th>
                  <th>Order Date</th>
                  <th>Site URL</th>
                  <th>Type</th>
                  <th>Earning</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {billing.placements.map((placement) => (
                  <tr key={placement.placementId}>
                    <td><span className="c-order">{placement.orderNumber}</span></td>
                    <td>
                      <span className="c-date">
                        {placement.orderDate
                          ? format(new Date(placement.orderDate), 'MMM dd, yyyy')
                          : 'N/A'}
                      </span>
                    </td>
                    <td>
                      <a href={placement.siteUrl || '#'} className="c-url" target="_blank" rel="noopener noreferrer">
                        {placement.siteUrl}
                      </a>
                    </td>
                    <td>
                      <span className={`type-badge ${
                        placement.submissionType === 'GBS' ? 'tb-gbs' : 
                        placement.submissionType.includes('EXTERNAL') ? 'tb-ext' : 
                        'tb-pr'
                      }`}>
                        {placement.submissionType}
                      </span>
                    </td>
                    <td><span className="c-earn">${placement.basePrice.toFixed(2)}</span></td>
                    <td><span className="status-badge">Completed</span></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ textAlign: 'right' }}>Total:</td>
                  <td>${billing.overallTotal.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </Box>
  );
};

export default PublisherBillingPage;
